import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  Select,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from 'native-base';
import { useSelector } from 'react-redux';
import * as UserApi from 'src/api/user';
import { IStore } from 'src/redux/reducers';
import { getText } from 'src/utils/i18n';
import { isElectron } from 'src/utils/miscUtils';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';

interface LightboxSettingsValue {
  sheetLengthCm: string;
  sheetWidthCm: string;
  backgroundColor: string;
}

const DEFAULT_LIGHTBOX_BACKGROUND_COLOR = 'blue';
const DEFAULT_LIGHTBOX_SHEET_WIDTH_CM = '22.5';
const DEFAULT_LIGHTBOX_SHEET_LENGTH_CM = '31.8';
const LIGHTBOX_BACKGROUND_COLOR_OPTIONS = ['blue', 'black', 'white'] as const;

const toInputValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const toOptionalNumber = (value: string, fallback?: number): number => {
  const trimmed = value.trim();
  if (!trimmed) {
    return fallback ?? 0;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : (fallback ?? 0);
};

const getInitialSettings = (
  stitchedSettings: {
    lightbox_sheet_length_cm?: number | string;
    lightbox_sheet_width_cm?: number | string;
    lightbox_background_color?: string;
  } | undefined
): LightboxSettingsValue => ({
  sheetLengthCm: toInputValue(stitchedSettings?.lightbox_sheet_length_cm) || DEFAULT_LIGHTBOX_SHEET_LENGTH_CM,
  sheetWidthCm: toInputValue(stitchedSettings?.lightbox_sheet_width_cm) || DEFAULT_LIGHTBOX_SHEET_WIDTH_CM,
  backgroundColor: toInputValue(stitchedSettings?.lightbox_background_color) || DEFAULT_LIGHTBOX_BACKGROUND_COLOR,
});

export default function SeedContourSettingsSection() {
  const toast = useToast();
  const userProfile = useSelector((store: IStore) => store.user.userProfile.response);
  const username = userProfile?.username;
  const stitchedSettings = React.useMemo(() => ({
    ...(userProfile?.settings || {}),
    lightbox_enabled: userProfile?.lightbox_enabled ?? userProfile?.settings?.lightbox_enabled,
    lightbox_background_color: userProfile?.lightbox_background_color ?? userProfile?.settings?.lightbox_background_color,
    lightbox_sheet_width_cm: userProfile?.lightbox_sheet_width_cm ?? userProfile?.settings?.lightbox_sheet_width_cm,
    lightbox_sheet_length_cm: userProfile?.lightbox_sheet_length_cm ?? userProfile?.settings?.lightbox_sheet_length_cm,
  }), [userProfile]);
  const lightboxEnabled = isLightboxFeatureEnabled(stitchedSettings);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');

  const [formValues, setFormValues] = React.useState<LightboxSettingsValue>(
    getInitialSettings(stitchedSettings)
  );
  const [hasLocalOverride, setHasLocalOverride] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!hasLocalOverride) {
      setFormValues(getInitialSettings(stitchedSettings));
    }
  }, [hasLocalOverride, stitchedSettings]);

  if (!lightboxEnabled) {
    return null;
  }

  const handleSave = async () => {
    if (!username) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        username,
        lightbox_background_color: formValues.backgroundColor.trim() || DEFAULT_LIGHTBOX_BACKGROUND_COLOR,
        lightbox_sheet_width_cm: toOptionalNumber(formValues.sheetWidthCm, 22.5),
        lightbox_sheet_length_cm: toOptionalNumber(formValues.sheetLengthCm, 31.8),
      };

      await UserApi.patchUserSettings(payload);
      setHasLocalOverride(true);

      toast.show({
        description: getText('lightboxSettingsSaved'),
        placement: 'bottom',
      });
    } catch (error) {
      console.error('Error saving lightbox settings:', error);
      toast.show({
        description: getText('lightboxSettingsSaveFailed'),
        placement: 'bottom',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VStack space={2}>
      <Text style={{ fontSize: 18, fontWeight: '600' }}>
        {getText('seedContourSettingsTitle')}
      </Text>
      <Box
        bg={cardBg}
        borderWidth={1}
        borderColor={borderColor}
        borderRadius={isElectron() ? 'xl' : 'lg'}
        p={isElectron() ? 6 : 4}
        shadow={isElectron() ? '2' : '0'}
      >
        <VStack space={4}>
          <Text fontSize="xs" color={secondaryTextColor}>
            {getText('seedContourSettingsDescription')}
          </Text>
          <VStack space={4}>
            <FormControl>
              <FormControl.Label>{getText('lightboxSheetLengthLabel')}</FormControl.Label>
              <Input
                keyboardType="decimal-pad"
                value={formValues.sheetLengthCm}
                onChangeText={(value) => setFormValues((current) => ({ ...current, sheetLengthCm: value }))}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>{getText('lightboxSheetWidthLabel')}</FormControl.Label>
              <Input
                keyboardType="decimal-pad"
                value={formValues.sheetWidthCm}
                onChangeText={(value) => setFormValues((current) => ({ ...current, sheetWidthCm: value }))}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>{getText('lightboxBackgroundColorLabel')}</FormControl.Label>
              <Select
                selectedValue={formValues.backgroundColor}
                onValueChange={(value) => setFormValues((current) => ({ ...current, backgroundColor: value }))}
              >
                {LIGHTBOX_BACKGROUND_COLOR_OPTIONS.map((value) => (
                  <Select.Item
                    key={value}
                    label={value.charAt(0).toUpperCase() + value.slice(1)}
                    value={value}
                  />
                ))}
              </Select>
            </FormControl>

            <Button
              onPress={handleSave}
              isLoading={isSaving}
              alignSelf="flex-start"
            >
              {getText('saveChanges')}
            </Button>
          </VStack>
        </VStack>
      </Box>
    </VStack>
  );
}
